/**
 * What the find-the-best-weapon criterion ranks candidates on.
 *
 * Per AP is how efficient the weapon is whatever the turn looks like; per hit
 * is one swing as it lands; per turn is what a turn of hitting with it is worth
 * at its best — every cast the game allows, as if AP were free. Only per AP
 * prices what a weapon costs: the other two ask how much damage the weapon can
 * do, not how much this build can currently afford. Mirrors `WEAPON_METRICS` in
 * `gen/engine/weapon_pool.py`.
 *
 * Its own module rather than a constant on Build: `kernelRequest` needs it, and
 * `Build` already reaches `kernelRequest` through `setBonusesFromCatalog`, so
 * importing it from there closes an import cycle that leaves CHARAC_IDS
 * undefined at load.
 */
export const BEST_WEAPON_METRIC_AP = 'ap';
export const BEST_WEAPON_METRIC_HIT = 'hit';
export const BEST_WEAPON_METRIC_TURN = 'turn';
export const BEST_WEAPON_METRICS = [
    BEST_WEAPON_METRIC_AP,
    BEST_WEAPON_METRIC_HIT,
    BEST_WEAPON_METRIC_TURN,
];
export const BEST_WEAPON_METRIC_DEFAULT = BEST_WEAPON_METRIC_AP;

/**
 * What each metric is called, in one place: the card's dropdown names it, and
 * so does the criterion's chip in the generator bar.
 */
const METRIC_LABELS = {
    [BEST_WEAPON_METRIC_AP]: { fr: 'Par PA', en: 'Per AP', de: 'Pro AP', es: 'Por PA', pt: 'Por PA' },
    [BEST_WEAPON_METRIC_HIT]: { fr: 'Par coup', en: 'Per hit', de: 'Pro Treffer', es: 'Por golpe', pt: 'Por golpe' },
    [BEST_WEAPON_METRIC_TURN]: { fr: 'Par tour', en: 'Per turn', de: 'Pro Zug', es: 'Por turno', pt: 'Por turno' },
};

export function bestWeaponMetricLabel(metric, lang = 'fr') {
    const labels = METRIC_LABELS[normalizeBestWeaponMetric(metric)];
    return labels[lang] || labels.en;
}

export function normalizeBestWeaponMetric(raw) {
    const v = String(raw || '').trim().toLowerCase();
    return BEST_WEAPON_METRICS.includes(v) ? v : BEST_WEAPON_METRIC_DEFAULT;
}

/**
 * How many times a turn can swing this weapon, AP no object — mirrors
 * `hits_per_turn` in `solver-core/src/weapon_pool.rs`.
 */
export function hitsPerTurn(maxCast) {
    return Math.max(1, Number(maxCast) || 1);
}

/** What a weapon card row is worth on `metric`. */
export function bestWeaponValue(weaponRow, metric) {
    const total = Number(weaponRow?.cardPrimaryTotal) || 0;
    if (metric === BEST_WEAPON_METRIC_TURN) return total * hitsPerTurn(weaponRow?.maxCast);
    // Per hit: the blow as it lands, neither divided by what it cost nor
    // multiplied by how often it can be repeated.
    if (metric === BEST_WEAPON_METRIC_HIT) return total;
    return total / Math.max(Number(weaponRow?.apCost) || 1, 1);
}
